import { client } from '@api/rest/client';
import MaterialIcon from '@components/common/MaterialIcon';
import { Spinner } from '@components/common/Spinner';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { ModalHeader } from '@components/tailwind-components/Modal';
import { Modal } from '@components/tailwind-components/Modal/Modal';
import type { TConversationThreadMessageDTO } from '@conversed/rest/src/contracts/train-ai/train-ai.dto';
import { purify } from '@helpers/domHelper';
import { getHotTopicDisplayName } from '@helpers/hotTopicHelper';
import { toastErrorTranslated } from '@helpers/toast';
import clsx from 'clsx';
import parse from 'html-react-parser';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationReviewThreadStats } from './ConversationReviewThreadStats';

// * Follow-up turns hang off the first question like a tree. Each one draws its own
// * vertical segment up through the gap-2 above it, so the segments join into one line,
// * plus a tick into the middle of the card; the last one stops the line at its tick. Pseudo-elements
// * are placed from the padding box, so every offset adds the card's 1px border - that
// * keeps the line touching the neighbouring borders without running onto them.
const THREAD_CONNECTOR_CLASS =
  'relative ml-8 before:absolute before:left-[calc(-1rem_-_1px)] before:top-[calc(-0.5rem_-_1px)] before:h-[calc(100%_+_0.5rem_+_2px)] before:border-l before:border-gray-300 last:before:h-[calc(50%_+_0.5rem_+_1px)] after:absolute after:left-[calc(-1rem_-_1px)] after:top-1/2 after:-translate-y-1/2 after:w-4 after:border-t after:border-gray-300';

type Props = {
  // The question being reviewed; null keeps the dialog closed.
  conversationId: string | null;
  onClose: () => void;
};

/**
 * Shows the whole conversation a reviewed question came from, with that question
 * highlighted. Rendered as its own Modal (nested HeadlessUI dialog) rather than through
 * showModal, so the review modal underneath keeps its draft state while this is open.
 * Header, background and rounding mirror the v2 modal defaults in ModalContainer, which
 * the Home v2 Intelligence topic modal uses.
 */
export const ConversationReviewThreadDialog: React.FC<Props> = ({
  conversationId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<TConversationThreadMessageDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const highlightedMessageRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    let isCancelled = false;
    setMessages([]);
    setIsLoading(true);

    client.trainAI
      .getConversationThread({ params: { conversationId } })
      .then(({ data }) => {
        if (!isCancelled) {
          setMessages(data.results);
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          toastErrorTranslated(error);
          onClose();
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [conversationId, onClose]);

  useEffect(() => {
    if (messages.length > 0) {
      highlightedMessageRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [messages]);

  return (
    <Modal
      className='px-[20px] py-[20px] max-h-[calc(100dvh-100px)] flex flex-col min-h-[175px]'
      isOpen={conversationId !== null}
      onClose={onClose}
      bgClass='bg-slate-50'
      roundedClass='rounded-2xl'
      size='lg'
      header={ModalHeader({
        content: t('train-ai.conversation-review.thread.title'),
        onClose,
        titleSize: 'text-lg',
        titleColor: 'text-gray-800',
      })}
      headerClasses='border-b-0'>
      <div className='flex flex-col gap-4'>
        <ConversationReviewThreadStats
          // Remount per conversation so an expanded topic list starts collapsed again.
          key={conversationId}
          messages={messages}
          isLoading={isLoading}
        />

        <div className='border-t border-gray-200 pt-4'>
          <p className='mb-3 text-sm font-medium text-gray-700'>
            {t('train-ai.conversation-review.thread.questions-label')}
          </p>

          {isLoading ? (
            <div className='flex justify-center py-6'>
              <Spinner />
            </div>
          ) : (
            <ul className='flex max-h-[50dvh] flex-col gap-2 overflow-y-auto pr-1'>
              {messages.map((message, messageIndex) => {
                const isHighlighted = message._id === conversationId;
                const isFollowUp = messageIndex > 0;

                return (
                  <li
                    key={message._id}
                    ref={isHighlighted ? highlightedMessageRef : undefined}
                    className={clsx(
                      'flex flex-col gap-0.5 rounded-lg border px-3 py-2.5',
                      isHighlighted
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-white',
                      isFollowUp && THREAD_CONNECTOR_CLASS,
                    )}>
                    {isHighlighted && (
                      <span className='mb-1 flex items-center gap-1 text-pico font-semibold uppercase tracking-wider text-blue-600'>
                        <MaterialIcon
                          icon='visibility'
                          type='outlined'
                          classes={['text-tiny', '!leading-none']}
                        />
                        {t('train-ai.conversation-review.thread.reviewing')}
                      </span>
                    )}
                    <div className='flex items-start justify-between gap-3'>
                      <span className='flex items-start gap-2'>
                        <MaterialIcon
                          icon='person'
                          type='outlined'
                          classes={[
                            'text-sm',
                            'text-gray-400',
                            'mt-0.5',
                            '!leading-none',
                          ]}
                        />
                        <span className='text-tiny text-gray-700'>
                          {message.question}
                        </span>
                      </span>
                      {message.hotTopic && (
                        <span className='shrink-0 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-timestamp font-medium text-purple-600'>
                          {getHotTopicDisplayName(message.hotTopic.name)}
                        </span>
                      )}
                    </div>
                    {message.answer && (
                      <div className='flex items-start gap-2'>
                        <MaterialIcon
                          icon='smart_toy'
                          type='outlined'
                          classes={[
                            'text-sm',
                            'text-primary-base',
                            'mt-0.5',
                            '!leading-none',
                          ]}
                        />
                        <span className='text-tiny text-gray-400 formatted-html-content'>
                          {parse(purify(message.answer))}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className='flex items-center border-t border-gray-200 pt-4'>
          <ButtonT
            type='button'
            styling='outline'
            size='xs'
            rounding='md'
            onClick={onClose}>
            {t('general.buttons.close')}
          </ButtonT>
        </div>
      </div>
    </Modal>
  );
};
